/**
 * The audit log. Admin only, because an investigator's queries are themselves sensitive.
 *
 * Each entry carries the hash that chains it to the one before, and the verify button
 * re-walks the whole chain server-side. A log that cannot show it is unbroken is a log
 * someone can edit — and the first thing anyone tampering with an investigation would
 * reach for is the record of what they looked at.
 */

import { useEffect, useState } from "react";
import { api, type AuditEntry } from "../lib/api";

export function Audit() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [verdict, setVerdict] = useState<Awaited<ReturnType<typeof api.auditVerify>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.audit(200).then(setEntries).catch((e: Error) => setError(e.message));
  }, []);

  async function verify() {
    setError(null);
    try {
      setVerdict(await api.auditVerify());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <main className="page">
      <h1>Audit log</h1>

      <div className="row">
        <button onClick={() => void verify()} data-testid="verify-chain">
          Verify the hash chain
        </button>
        {verdict && (
          <span
            data-testid="verify-result"
            style={{ color: verdict.intact ? "var(--human)" : "var(--danger)" }}
          >
            {verdict.intact
              ? `${verdict.entries} entries, chain intact`
              : `broken at entry ${verdict.first_break_id}: ${verdict.reason}`}
          </span>
        )}
      </div>

      {error && (
        <p data-testid="audit-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      <div className="table-scroll">
        <table className="grid" data-testid="audit-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Detail</th>
              <th>Entry hash</th>
            </tr>
          </thead>
          <tbody>
            {entries?.map((entry) => (
              <tr key={entry.id} data-testid="audit-row" data-action={entry.action}>
                <td className="mono">{entry.occurred_at}</td>
                <td>{entry.action}</td>
                <td className="mono">{entry.actor_id?.slice(0, 8) ?? "—"}</td>
                <td className="mono detail">{JSON.stringify(entry.detail)}</td>
                <td className="mono" title={entry.entry_hash}>
                  {entry.entry_hash.slice(0, 12)}…
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
